import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Star, ExternalLink, ArrowLeft, BadgeCheck, Check, Globe, Users, Calendar, Shield, GitCompare, Loader2, Edit, Trash2 } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import AuthModal from '../components/AuthModal';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useTool, useTools } from '../hooks/useData';
import { useReviews } from '../hooks/useReviews';
import { useCompare } from '../context/CompareContext';
import { useUser } from '../context/UserContext';
import { toast } from 'sonner';

const ToolDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { tool, loading } = useTool(slug);
  const { tools } = useTools();
  const { reviews, loading: reviewsLoading, addReview, deleteReview } = useReviews(slug);
  const { toggleCompare, isInCompare, compareCount } = useCompare();
  const { user, isAuthenticated, token } = useUser();

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, content: '' });
  const [submittingReview, setSubmittingReview] = useState(false);

  const handleToggleCompare = () => {
    if (!tool) return;
    const result = toggleCompare(tool);
    if (result.success) {
      toast.success(result.message);
    } else {
      toast.error(result.message);
    }
  };

  const handleCompareNow = () => {
    if (tool && !isInCompare(tool.id)) {
      toggleCompare(tool);
    }
    navigate('/compare');
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    if (!reviewForm.content.trim()) {
      toast.error('Please write a review');
      return;
    }

    setSubmittingReview(true);
    try {
      await addReview({
        tool_id: tool.id,
        tool_slug: slug,
        rating: reviewForm.rating,
        content: reviewForm.content
      }, token);
      toast.success('Review submitted successfully!');
      setReviewForm({ rating: 5, content: '' });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit review';
      toast.error(message);
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete your review?')) return;
    
    try {
      await deleteReview(reviewId, token);
      toast.success('Review deleted');
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            <span className="ml-2 text-gray-600">Loading tool details...</span>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!tool) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 text-center py-20">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Tool not found</h1>
            <Button asChild>
              <Link to="/tools">Browse All Tools</Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedTools = tools
    .filter((t) => t.categorySlug === tool.categorySlug && t.id !== tool.id)
    .slice(0, 3);

  const userHasReviewed = reviews.some(r => r.user_id === user?.id);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="mb-6 flex items-center justify-between">
            <Link
              to="/tools"
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Tools
            </Link>
            {compareCount > 0 && (
              <Link to="/compare">
                <Button variant="outline" size="sm" data-testid="go-to-compare-header">
                  <GitCompare className="w-4 h-4 mr-2" />
                  Compare ({compareCount})
                </Button>
              </Link>
            )}
          </div>

          {/* Header */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Tool Info */}
              <div className="flex-1">
                <div className="flex items-start gap-6 mb-6">
                  <div className="w-20 h-20 rounded-2xl bg-gray-100 overflow-hidden">
                    <img
                      src={tool.logo}
                      alt={tool.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/100?text=' + tool.name.charAt(0);
                      }}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h1 className="text-3xl font-bold text-gray-900">
                        {tool.name}
                      </h1>
                      {tool.verified && (
                        <Badge className="bg-emerald-50 text-emerald-600 border-0">
                          <BadgeCheck className="w-4 h-4 mr-1" />
                          Verified
                        </Badge>
                      )}
                    </div>
                    <Link
                      to={`/category/${tool.categorySlug}`}
                      className="text-sm text-gray-500 hover:text-emerald-600"
                    >
                      {tool.category}
                    </Link>
                  </div>
                </div>

                <p className="text-lg text-gray-600 mb-6 whitespace-pre-line">{tool.description}</p>

                {/* Stats */}
                <div className="flex flex-wrap items-center gap-6 mb-6">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                    <span className="text-lg font-semibold text-gray-900">
                      {tool.rating}
                    </span>
                    <span className="text-gray-500">
                      ({reviews.length || tool.reviews || 0} reviews)
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-500">
                    <Users className="w-5 h-5" />
                    <span>10K+ users</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-500">
                    <Calendar className="w-5 h-5" />
                    <span>Added {tool.created_at ? new Date(tool.created_at).toLocaleDateString() : 'Recently'}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-4">
                  <Button
                    size="lg"
                    className="bg-emerald-500 hover:bg-emerald-600"
                    asChild
                  >
                    <a href={tool.website} target="_blank" rel="noopener noreferrer">
                      <Globe className="w-4 h-4 mr-2" />
                      Visit Website
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </a>
                  </Button>
                  <Button 
                    size="lg" 
                    variant={isInCompare(tool.id) ? "default" : "outline"}
                    onClick={handleToggleCompare}
                    className={isInCompare(tool.id) ? "bg-emerald-500 hover:bg-emerald-600" : ""}
                    data-testid="add-to-compare-btn"
                  >
                    {isInCompare(tool.id) ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Added to Compare
                      </>
                    ) : (
                      <>
                        <GitCompare className="w-4 h-4 mr-2" />
                        Add to Compare
                      </>
                    )}
                  </Button>
                  {isInCompare(tool.id) && (
                    <Button 
                      size="lg" 
                      variant="outline"
                      onClick={handleCompareNow}
                      data-testid="compare-now-btn"
                    >
                      Compare Now →
                    </Button>
                  )}
                </div>
              </div>

              {/* Pricing Card */}
              <div className="lg:w-80">
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">
                    Pricing
                  </h3>
                  <div className="text-2xl font-bold text-gray-900 mb-4">
                    {tool.pricing}
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="w-4 h-4 text-emerald-500" />
                      Free trial available
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="w-4 h-4 text-emerald-500" />
                      No credit card required
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Shield className="w-4 h-4 text-emerald-500" />
                      Enterprise plans available
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs Content */}
          <Tabs defaultValue="reviews" className="mb-8">
            <TabsList className="bg-white border border-gray-100 p-1 rounded-xl">
              <TabsTrigger value="reviews" className="rounded-lg">
                Reviews ({reviews.length})
              </TabsTrigger>
              <TabsTrigger value="features" className="rounded-lg">
                Features
              </TabsTrigger>
              <TabsTrigger value="alternatives" className="rounded-lg">
                Alternatives
              </TabsTrigger>
            </TabsList>

            <TabsContent value="reviews" className="mt-6">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  User Reviews
                </h2>

                {/* Write Review Form */}
                {!userHasReviewed && (
                  <div className="mb-8 p-6 bg-gray-50 rounded-xl">
                    <h3 className="font-medium text-gray-900 mb-4">Write a Review</h3>
                    <form onSubmit={handleSubmitReview}>
                      {/* Rating Selection */}
                      <div className="mb-4">
                        <label className="block text-sm text-gray-600 mb-2">Your Rating</label>
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                              className="p-1 transition-transform hover:scale-110"
                            >
                              <Star
                                className={`w-6 h-6 ${
                                  star <= reviewForm.rating
                                    ? 'text-amber-400 fill-amber-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Review Content */}
                      <div className="mb-4">
                        <Textarea
                          placeholder="Share your experience with this tool..."
                          value={reviewForm.content}
                          onChange={(e) => setReviewForm({ ...reviewForm, content: e.target.value })}
                          rows={4}
                          className="resize-none"
                        />
                      </div>

                      <Button
                        type="submit"
                        className="bg-emerald-500 hover:bg-emerald-600"
                        disabled={submittingReview}
                      >
                        {submittingReview ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : null}
                        {isAuthenticated ? 'Submit Review' : 'Sign in to Review'}
                      </Button>
                    </form>
                  </div>
                )}

                {/* Reviews List */}
                {reviewsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                  </div>
                ) : reviews.length > 0 ? (
                  <div className="space-y-6">
                    {reviews.map((review) => (
                      <div key={review.id} className="p-6 bg-gray-50 rounded-xl">
                        <div className="flex items-start gap-4 mb-4">
                          <img
                            src={review.user_avatar || `https://ui-avatars.com/api/?name=${review.user_name}&background=10b981&color=fff`}
                            alt={review.user_name}
                            className="w-10 h-10 rounded-full"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">
                              {review.user_name}
                            </div>
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, j) => (
                                <Star
                                  key={j}
                                  className={`w-4 h-4 ${
                                    j < review.rating
                                      ? 'text-amber-400 fill-amber-400'
                                      : 'text-gray-200'
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-400">
                              {new Date(review.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </span>
                            {user?.id === review.user_id && (
                              <button
                                onClick={() => handleDeleteReview(review.id)}
                                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                title="Delete review"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-gray-600">{review.content}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <p className="mb-2">No reviews yet</p>
                    <p className="text-sm">Be the first to review this tool!</p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="features" className="mt-6">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  Key Features
                </h2>
                {tool.features && tool.features.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {tool.features.map((feature, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl"
                      >
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Check className="w-5 h-5 text-emerald-600" />
                        </div>
                        <span className="font-medium text-gray-900">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No features listed yet.</p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="alternatives" className="mt-6">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  Similar Tools
                </h2>
                {relatedTools.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {relatedTools.map((t) => (
                      <Link
                        key={t.id}
                        to={`/tool/${t.slug}`}
                        className="group p-4 border border-gray-100 rounded-xl hover:border-gray-200 hover:shadow-lg transition-all"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <img
                            src={t.logo}
                            alt={t.name}
                            className="w-12 h-12 rounded-lg object-cover"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/100?text=' + t.name.charAt(0);
                            }}
                          />
                          <div>
                            <h4 className="font-semibold text-gray-900 group-hover:text-emerald-600">
                              {t.name}
                            </h4>
                            <div className="flex items-center gap-1">
                              <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                              <span className="text-sm text-gray-500">
                                {t.rating}
                              </span>
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-500 line-clamp-2">
                          {t.description}
                        </p>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">
                    No similar tools found in this category.
                  </p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
      
      {/* Auth Modal */}
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
};

export default ToolDetail;
